import apiClient from '../lib/apiClient';
import { getStoredAuthToken } from '../config/auth';

const extractData = (promise) => promise.then((response) => response.data);
const TASKS_SEARCH_TIMEOUT_MS = 120000;

export const fetchDashboardKpi = (params) =>
  extractData(apiClient.get('/analytics/dashboard/kpi', { params }));

export const fetchCallDynamics = (params) =>
  extractData(apiClient.get('/analytics/dashboard/call-dynamics', { params }));

export const fetchComplaintsNegativity = (params) =>
  extractData(apiClient.get('/analytics/dashboard/complaints-negativity', { params }));

export const fetchCallTopics = (params) =>
  extractData(apiClient.get('/analytics/dashboard/call-topics', { params }));

export const getCallClassificationTypes = () =>
  extractData(apiClient.get('/analytics/dashboard/call-classification-types'));

export const fetchUpsellFunnel = (params) =>
  extractData(apiClient.get('/analytics/dashboard/upsell-funnel', { params }));

export const fetchEmployeeKpi = (params) =>
  extractData(apiClient.get('/analytics/employees/kpi', { params }));

export const fetchEmployeeEfficiency = (params) =>
  extractData(apiClient.get('/analytics/employees/efficiency', { params }));

export const fetchEmployeeDailyWorkload = (params) =>
  extractData(apiClient.get('/analytics/employees/workload/daily', { params }));

export const fetchEmployeeHourlyWorkload = (params) =>
  extractData(apiClient.get('/analytics/employees/workload/hourly', { params }));

export const fetchEmployeeHourlyWorkloadByDay = (params) =>
  extractData(apiClient.get('/analytics/employees/workload/hourly-by-day', { params }));

export const fetchEmployeeHourlyWaitWorkload = (params) =>
  extractData(apiClient.get('/analytics/employees/workload/hourly-wait', { params }));

export const fetchEmployeeCallsDynamics = (params) =>
  extractData(apiClient.get('/analytics/employees/calls-dynamics', { params }));

export const fetchEmployeeQualityTrend = (params) =>
  extractData(apiClient.get('/analytics/employees/quality-trend', { params }));

export const fetchEmployeeUpsellFunnel = (params) =>
  extractData(apiClient.get('/analytics/employees/upsell-funnel', { params }));

export const fetchEmployees = (params) =>
  extractData(apiClient.get('/employees', { params }));

export const restoreEmployeesFromHistory = () =>
  extractData(apiClient.post('/employees/restore-from-history'));

export const fetchCalls = (params) =>
  extractData(apiClient.get('/analytics/calls', { params }));

export const fetchMissedCalls = (params) =>
  extractData(apiClient.get('/missed-calls', { params }));

export const fetchMissedCallOrders = (payload) =>
  extractData(apiClient.post('/missed-calls/orders/lookup', payload));

export const refreshMissedCallOrder = (payload) =>
  extractData(apiClient.post('/missed-calls/orders/refresh', payload));

export const claimMissedCall = (clientPhone, params) =>
  extractData(apiClient.post(`/missed-calls/groups/${encodeURIComponent(clientPhone)}/claim`, null, { params }));

export const unclaimMissedCall = (clientPhone, params) =>
  extractData(apiClient.post(`/missed-calls/groups/${encodeURIComponent(clientPhone)}/unclaim`, null, { params }));

export const markMissedCallProcessed = (clientPhone, params) =>
  extractData(apiClient.post(`/missed-calls/groups/${encodeURIComponent(clientPhone)}/mark-processed`, null, { params }));

export const unmarkMissedCallProcessed = (clientPhone, params) =>
  extractData(apiClient.post(`/missed-calls/groups/${encodeURIComponent(clientPhone)}/unmark-processed`, null, { params }));

export const sendMissedCallNotification = (entryId) =>
  extractData(apiClient.post(`/missed-calls/${entryId}/notify`));

export const fetchCallFilterOptions = (params) =>
  extractData(apiClient.get('/analytics/calls/filter-options', { params }));

export const startCallsExport = (params, force = false) =>
  extractData(apiClient.post('/analytics/calls/export', null, {
    params: { ...params, force }
  }));

export const listCallsExportJobs = () =>
  extractData(apiClient.get('/analytics/calls/export/jobs'));

export const getCallsExportJob = (id) =>
  extractData(apiClient.get(`/analytics/calls/export/jobs/${id}`));

export const downloadCallsExportJob = async (id, fallbackFilename) => {
  const response = await apiClient.get(`/analytics/calls/export/jobs/${id}/download`, {
    responseType: 'blob',
    timeout: 600000
  });

  const blob = response.data;
  const filename = resolveDownloadFilename(response.headers?.['content-disposition'], fallbackFilename);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const deleteCallsExportJob = (id) =>
  extractData(apiClient.delete(`/analytics/calls/export/jobs/${id}`));

const resolveDownloadFilename = (contentDisposition, fallbackFilename) => {
  if (fallbackFilename) {
    return fallbackFilename;
  }

  const utf8Match = contentDisposition?.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const plainMatch = contentDisposition?.match(/filename="?([^";]+)"?/i);
  if (plainMatch?.[1]) {
    return plainMatch[1];
  }

  return 'calls-export.xlsx';
};

export const fetchComplaints = (params) =>
  extractData(apiClient.get('/analytics/complaints', { params }));

export const fetchComplaintsSummary = (params) =>
  extractData(apiClient.get('/analytics/complaints/summary', { params }));

export const fetchComplaintsPoints = (params) =>
  extractData(apiClient.get('/analytics/complaints/points', { params }));

export const fetchComplaintsOptions = () =>
  extractData(apiClient.get('/analytics/complaints/options'));

export const fetchComplaintCase = (phone, date) =>
  extractData(apiClient.get(`/analytics/complaints/${encodeURIComponent(phone)}/${date}`));

export const updateComplaintReview = (phone, date, payload) =>
  extractData(apiClient.put(`/analytics/complaints/${encodeURIComponent(phone)}/${date}/review`, payload));

export const fetchComplaintHistory = (phone, date) =>
  extractData(apiClient.get(`/analytics/complaints/${encodeURIComponent(phone)}/${date}/history`));

export const startComplaintsExport = (params, force = false) =>
  extractData(apiClient.post('/analytics/complaints/export', null, {
    params: { ...params, force }
  }));

export const listComplaintsExportJobs = () =>
  extractData(apiClient.get('/analytics/complaints/export/jobs'));

export const getComplaintsExportJob = (id) =>
  extractData(apiClient.get(`/analytics/complaints/export/jobs/${id}`));

export const downloadComplaintsExportJob = async (id, fallbackFilename) => {
  const response = await apiClient.get(`/analytics/complaints/export/jobs/${id}/download`, {
    responseType: 'blob',
    timeout: 600000
  });

  const blob = response.data;
  const filename = resolveDownloadFilename(response.headers?.['content-disposition'], fallbackFilename);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const deleteComplaintsExportJob = (id) =>
  extractData(apiClient.delete(`/analytics/complaints/export/jobs/${id}`));

export const startComplaintsPointsRefresh = (params, force = false) =>
  extractData(apiClient.post('/analytics/complaints/points-refresh', null, {
    params: { ...params, force }
  }));

export const listComplaintsPointsRefreshJobs = () =>
  extractData(apiClient.get('/analytics/complaints/points-refresh/jobs'));

export const getComplaintsPointsRefreshJob = (id) =>
  extractData(apiClient.get(`/analytics/complaints/points-refresh/jobs/${id}`));

export const deleteComplaintsPointsRefreshJob = (id) =>
  extractData(apiClient.delete(`/analytics/complaints/points-refresh/jobs/${id}`));

export const fetchClientsStats = (params) =>
  extractData(apiClient.get('/clients/stats', { params }));

export const fetchClients = (params) =>
  extractData(apiClient.get('/clients', { params }));

export const createClient = (payload) =>
  extractData(apiClient.post('/clients', payload));

export const createEmployee = (payload) =>
  extractData(apiClient.post('/employees', payload));

export const updateEmployee = (id, payload) =>
  extractData(apiClient.put(`/employees/${id}`, payload));

export const updateClient = (id, payload) =>
  extractData(apiClient.put(`/clients/${id}`, payload));

export const deleteClient = (id) =>
  extractData(apiClient.delete(`/clients/${id}`));

export const fetchAnalyticsClientsStats = (params) =>
  extractData(apiClient.get('/analytics/clients/stats', { params }));

export const fetchAnalyticsClients = (params) =>
  extractData(apiClient.get('/analytics/clients', { params }));

export const fetchClientOrders = (clientId, params) =>
  extractData(apiClient.get(`/analytics/clients/${clientId}/orders`, { params }));

export const refreshClientOrders = (clientId, params) =>
  extractData(apiClient.post(`/analytics/clients/${clientId}/orders/refresh`, null, { params }));

export const fetchClientSummary = (clientId, params) =>
  extractData(apiClient.get(`/analytics/clients/${clientId}/summary`, { params }));

export const fetchEvaluationScore = (callId) =>
  extractData(apiClient.get('/analytics/evaluations/score', { params: { call_id: callId } }));

export const fetchTasks = (params) =>
  extractData(
    apiClient.get('/tasks', {
      params,
      timeout: TASKS_SEARCH_TIMEOUT_MS
    })
  );

export const fetchTasksSummary = (params) =>
  extractData(apiClient.get('/tasks/summary', { params }));

export const fetchTaskStatus = (taskId) =>
  extractData(apiClient.get(`/tasks/${taskId}/status`));

export const restartTask = (taskId, { fromCheckpoint = true } = {}) =>
  extractData(
    apiClient.post(`/tasks/${taskId}/restart`, null, {
      params: { fromCheckpoint }
    })
  );

export const deleteTask = (taskId) =>
  extractData(apiClient.delete(`/tasks/${taskId}`));

export const bulkRestartTasks = (filterParams, { fromCheckpoint = false } = {}) =>
  extractData(
    apiClient.post('/tasks/bulk-restart', null, {
      params: { ...filterParams, fromCheckpoint },
      timeout: TASKS_SEARCH_TIMEOUT_MS
    })
  );

export const downloadTasksArtifacts = (filterParams) =>
  apiClient
    .get('/tasks/bulk-artifacts', {
      params: filterParams,
      responseType: 'blob',
      timeout: 600000
    })
    .then((response) => response.data);

export const createAnalyticalTasks = ({
  files,
  employeeId,
  clientId,
  callStartedAtUtc,
  callEndedAtUtc,
  durationSeconds,
  callDirection,
  topic,
  notes
}) => {
  const formData = new FormData();
  const payloadFiles = Array.isArray(files) ? files : files ? [files] : [];

  payloadFiles.forEach((item) => {
    if (item) {
      formData.append('files', item);
    }
  });

  if (payloadFiles.length === 1) {
    formData.append('file', payloadFiles[0]);
  }

  if (employeeId) {
    formData.append('employeeId', employeeId);
  }
  if (clientId) {
    formData.append('clientId', clientId);
  }
  if (callStartedAtUtc) {
    formData.append('callStartedAtUtc', callStartedAtUtc);
  }
  if (callEndedAtUtc) {
    formData.append('callEndedAtUtc', callEndedAtUtc);
  }
  if (durationSeconds) {
    formData.append('durationSeconds', durationSeconds);
  }
  if (callDirection) {
    formData.append('callDirection', callDirection);
  }
  if (topic) {
    formData.append('topic', topic);
  }
  if (notes) {
    formData.append('notes', notes);
  }

  return extractData(
    apiClient.post('/tasks', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  );
};

export const fetchTaskTranscript = (taskId) =>
  apiClient
    .get(`/tasks/${taskId}/transcript`, { responseType: 'text' })
    .then((response) => response.data);

export const fetchTaskAnalysis = (taskId) =>
  extractData(apiClient.get(`/tasks/${taskId}/analysis`));

export const getTaskAudioUrl = (taskId) => {
  const token = getStoredAuthToken();
  const tokenParam = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${apiClient.defaults.baseURL}/tasks/${taskId}/audio${tokenParam}`;
};

export const fetchMetadataTypes = () =>
  extractData(apiClient.get('/metadata/types'));

export const createMetadataType = (payload) =>
  extractData(apiClient.post('/metadata/types', payload));

export const updateMetadataType = (key, payload) =>
  extractData(apiClient.put(`/metadata/types/${key}`, payload));

export const deleteMetadataType = (key) =>
  extractData(apiClient.delete(`/metadata/types/${key}`));

export const fetchMetadataItems = (type) =>
  extractData(apiClient.get(`/metadata/types/${type}/items`));

export const createMetadataItem = (type, payload) =>
  extractData(apiClient.post(`/metadata/types/${type}/items`, payload));

export const updateMetadataItem = (type, id, payload) =>
  extractData(apiClient.put(`/metadata/types/${type}/items/${id}`, payload));

export const deleteMetadataItem = (type, id) =>
  extractData(apiClient.delete(`/metadata/types/${type}/items/${id}`));

export const fetchPrompts = (includeDisabled = true) =>
  extractData(apiClient.get('/metadata/prompts', { params: { includeDisabled } }));

export const createPrompt = (payload) =>
  extractData(apiClient.post('/metadata/prompts', payload));

export const updatePrompt = (id, payload) =>
  extractData(apiClient.put(`/metadata/prompts/${id}`, payload));

export const deletePrompt = (id) =>
  extractData(apiClient.delete(`/metadata/prompts/${id}`));

export const cleanupStorage = (payload) =>
  extractData(apiClient.post('/maintenance/storage/cleanup', payload));

export const fetchTimeZoneSetting = () =>
  extractData(apiClient.get('/settings/timezone'));

export const updateTimeZoneSetting = (timeZone) =>
  apiClient.put('/settings/timezone', { timeZone });

export const fetchLinguisticPipelineSetting = () =>
  extractData(apiClient.get('/settings/linguistic-pipeline'));

export const updateLinguisticPipelineSetting = (enabled) =>
  apiClient.put('/settings/linguistic-pipeline', { enabled });

export const fetchEmployeesFromMango = () =>
  extractData(apiClient.post('/employees/fetch-from-mango'));

export const fetchUsers = () =>
  extractData(apiClient.get('/users'));

export const createUser = (payload) =>
  extractData(apiClient.post('/users', payload));

export const updateUser = (id, payload) =>
  extractData(apiClient.put(`/users/${id}`, payload));

export const changeUserPassword = (id, password) =>
  extractData(apiClient.post(`/users/${id}/password`, { password }));

export const deactivateUser = (id) =>
  extractData(apiClient.delete(`/users/${id}`));
